/**
 * AcceptConnectionPage
 *
 * Standalone page for accepting connection requests via email link.
 * Route: /connections/accept/:token
 *
 * States:
 * 1. Loading: Validating token...
 * 2. Valid: Show requester details + Accept/Decline buttons
 * 3. Expired: Show expiry message
 * 4. Invalid: Show not found message
 * 5. Already accepted/declined: Show appropriate message
 * 6. Login required: Prompt user to login first
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  UserCheck,
  UserX,
  Clock,
  AlertCircle,
  Loader2,
  LogIn,
  Home,
  Check,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/stores/auth/auth-store';
import { connectionsApi } from '@/services/api/connections';
import {
  FOREST_GREEN,
  ERROR,
  WARNING,
  BACKGROUND,
  MID_GRAY,
  SHADOWS,
} from '@/styles/tokens';
import { TerraMatrixBrand } from '@/components/branding/TerraMatrixBrand';
import type { ConnectionRequestDetails, ConnectionAcceptResult } from '@/types/connections';
import * as errorUtils from '@/utils/errors';

type PageState =
  | 'loading'
  | 'valid'
  | 'expired'
  | 'invalid'
  | 'already_accepted'
  | 'already_declined'
  | 'processing'
  | 'success'
  | 'error'
  | 'login_required';

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * AcceptConnectionPage Component
 */
export const AcceptConnectionPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { isAuthenticated } = useAuth();

  const [pageState, setPageState] = useState<PageState>('loading');
  const [requestDetails, setRequestDetails] = useState<ConnectionRequestDetails | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [_acceptResult, setAcceptResult] = useState<ConnectionAcceptResult | null>(null);

  // Validate the token
  const validateToken = useCallback(async () => {
    if (!token) {
      console.log('[AcceptConnectionPage] No token provided');
      setPageState('invalid');
      setErrorMessage('No connection request token provided.');
      return;
    }

    console.log('[AcceptConnectionPage] Validating token:', token.substring(0, 8) + '...');

    try {
      const response = await connectionsApi.getRequestByToken(token);
      const details = response.data;

      if (details) {
        setRequestDetails(details);
        console.log('[AcceptConnectionPage] Token status:', details.status);

        if (details.status === 'already_accepted') {
          setPageState('already_accepted');
        } else if (details.status === 'already_declined') {
          setPageState('already_declined');
        } else if (!isAuthenticated) {
          setPageState('login_required');
        } else {
          setPageState('valid');
        }
      } else {
        setPageState('invalid');
        setErrorMessage('Invalid connection request link.');
      }
    } catch (error: unknown) {
      console.error('[AcceptConnectionPage] Validation error:', error);

      const status = errorUtils.getErrorStatus(error);
      const message = errorUtils.getApiErrorMessage(error, '');

      if (status === 410) {
        setPageState('expired');
        setErrorMessage(message || 'This connection request has expired.');
      } else if (status === 404) {
        setPageState('invalid');
        setErrorMessage(message || 'This connection request link is invalid or has already been used.');
      } else {
        setPageState('error');
        setErrorMessage(message || 'Failed to validate connection request. Please try again.');
      }
    }
  }, [token, isAuthenticated]);

  // Validate on mount and when authentication changes
  useEffect(() => {
    validateToken();
  }, [validateToken]);

  // Handle accept
  const handleAccept = async () => {
    if (!token) return;

    setPageState('processing');
    console.log('[AcceptConnectionPage] Accepting connection request...');

    try {
      const response = await connectionsApi.acceptByToken(token);
      setAcceptResult(response.data ?? null);
      setPageState('success');

      const displayName = requestDetails?.requester?.firstName && requestDetails?.requester?.lastName
        ? `${requestDetails.requester.firstName} ${requestDetails.requester.lastName}`
        : requestDetails?.requester?.username || 'User';

      toast.success(`Connected with ${displayName}!`, {
        description: 'You can now see each other in your connections.',
      });

      // Auto-redirect after a short delay
      setTimeout(() => {
        navigate('/dashboard/connections');
      }, 2000);
    } catch (error: unknown) {
      console.error('[AcceptConnectionPage] Accept error:', error);
      setPageState('error');
      setErrorMessage(
        errorUtils.getApiErrorMessage(
          error,
          'Failed to accept connection request. Please try again.',
        )
      );

      toast.error('Failed to accept connection', {
        description: errorUtils.getApiErrorMessage(error, 'Please try again.'),
      });
    }
  };

  // Handle decline
  const handleDecline = async () => {
    if (!token) return;

    setPageState('processing');
    console.log('[AcceptConnectionPage] Declining connection request...');

    try {
      await connectionsApi.declineByToken(token);

      toast.success('Connection request declined');

      navigate(`/connections/decline/${token}?success=true`);
    } catch (error: unknown) {
      console.error('[AcceptConnectionPage] Decline error:', error);
      setPageState('valid');
      setErrorMessage(
        errorUtils.getApiErrorMessage(
          error,
          'Failed to decline connection request. Please try again.',
        )
      );

      toast.error('Failed to decline connection', {
        description: errorUtils.getApiErrorMessage(error, 'Please try again.'),
      });
    }
  };

  // Handle login redirect
  const handleLogin = () => {
    sessionStorage.setItem('connection_redirect', window.location.pathname);
    navigate('/login');
  };

  // Format expiry date
  const formatExpiry = (expiresAt: string): string => {
    const expiry = new Date(expiresAt);
    return expiry.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Get requester display name
  const getRequesterDisplayName = () => {
    if (!requestDetails?.requester) return 'Unknown User';
    const { firstName, lastName, username } = requestDetails.requester;
    if (firstName && lastName) return `${firstName} ${lastName}`;
    if (firstName) return firstName;
    return username;
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-8"
      style={{ backgroundColor: BACKGROUND.secondary }}
    >
      {/* Header with logo */}
      <div className="mb-8">
        <Link to="/">
          <TerraMatrixBrand
            markClassName="h-10 w-10"
            nameClassName="text-3xl"
          />
        </Link>
      </div>

      {/* Card container */}
      <div
        className="w-full max-w-md rounded-xl overflow-hidden"
        style={{
          backgroundColor: BACKGROUND.primary,
          boxShadow: SHADOWS.xl,
        }}
      >
        {/* Loading State */}
        {pageState === 'loading' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Validating Request</h2>
            <p className="text-muted-foreground">
              Please wait while we verify your connection request...
            </p>
          </div>
        )}

        {/* Valid State */}
        {pageState === 'valid' && requestDetails?.requester && (
          <>
            {/* Header */}
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <UserCheck className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">Connection Request</h2>
              <p className="text-sm opacity-80 mt-1">
                Someone wants to connect with you
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Requester info */}
              <div className="flex items-center gap-4 mb-6">
                <Avatar className="h-16 w-16">
                  <AvatarImage
                    src={requestDetails.requester.avatar}
                    alt={getRequesterDisplayName()}
                  />
                  <AvatarFallback
                    style={{
                      backgroundColor: FOREST_GREEN.faint,
                      color: FOREST_GREEN.DEFAULT,
                    }}
                    className="text-lg font-medium"
                  >
                    {getInitials(
                      requestDetails.requester.firstName,
                      requestDetails.requester.lastName,
                      requestDetails.requester.username
                    )}
                  </AvatarFallback>
                </Avatar>

                <div>
                  <h3 className="font-semibold text-lg">
                    {getRequesterDisplayName()}
                  </h3>
                  <p className="text-muted-foreground">
                    @{requestDetails.requester.username}
                  </p>
                </div>
              </div>

              {/* Request details */}
              <div className="space-y-3 mb-6">
                {requestDetails.requestedAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Requested</span>
                    <span>
                      {new Date(requestDetails.requestedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}

                {requestDetails.expiresAt && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Expires</span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatExpiry(requestDetails.expiresAt)}
                    </span>
                  </div>
                )}
              </div>

              {/* Personal message */}
              {requestDetails.message && (
                <div
                  className="p-4 rounded-lg mb-6 text-sm italic"
                  style={{ backgroundColor: BACKGROUND.secondary }}
                >
                  "{requestDetails.message}"
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={handleDecline}>
                  <UserX className="h-4 w-4 mr-2" />
                  Decline
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleAccept}
                  style={{
                    backgroundColor: FOREST_GREEN.DEFAULT,
                    color: 'white',
                  }}
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  Accept
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Login Required State */}
        {pageState === 'login_required' && requestDetails?.requester && (
          <>
            {/* Header */}
            <div
              className="p-6 text-center text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
            >
              <UserCheck className="h-12 w-12 mx-auto mb-3 opacity-90" />
              <h2 className="text-xl font-semibold">Connection Request</h2>
              <p className="text-sm opacity-80 mt-1">
                from {getRequesterDisplayName()}
              </p>
            </div>

            {/* Content */}
            <div className="p-6">
              <div className="text-center mb-6">
                <User
                  className="h-12 w-12 mx-auto mb-3"
                  style={{ color: MID_GRAY.DEFAULT }}
                />
                <h3 className="text-lg font-medium mb-2">Sign in to continue</h3>
                <p className="text-muted-foreground text-sm">
                  Please sign in with your TerraMatrix account to respond to this
                  connection request.
                </p>
              </div>

              {/* Login button */}
              <Button
                className="w-full"
                onClick={handleLogin}
                style={{
                  backgroundColor: FOREST_GREEN.DEFAULT,
                  color: 'white',
                }}
              >
                <LogIn className="h-4 w-4 mr-2" />
                Sign In to Respond
              </Button>
            </div>
          </>
        )}

        {/* Processing State */}
        {pageState === 'processing' && (
          <div className="p-8 text-center">
            <Loader2
              className="h-12 w-12 animate-spin mx-auto mb-4"
              style={{ color: FOREST_GREEN.DEFAULT }}
            />
            <h2 className="text-xl font-semibold mb-2">Processing...</h2>
            <p className="text-muted-foreground">
              Please wait while we process your response...
            </p>
          </div>
        )}

        {/* Success State */}
        {pageState === 'success' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <Check className="h-8 w-8" style={{ color: FOREST_GREEN.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Connected!</h2>
            <p className="text-muted-foreground mb-6">
              You are now connected with {getRequesterDisplayName()}.
            </p>
            <p className="text-sm text-muted-foreground mb-4">
              Redirecting you to your connections...
            </p>
            <Button
              onClick={() => navigate('/dashboard/connections')}
              style={{
                backgroundColor: FOREST_GREEN.DEFAULT,
                color: 'white',
              }}
            >
              View Connections
            </Button>
          </div>
        )}

        {/* Expired State */}
        {pageState === 'expired' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: WARNING.bg }}
            >
              <Clock className="h-8 w-8" style={{ color: WARNING.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Request Expired</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                'This connection request has expired. Please ask them to send a new request.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Invalid State */}
        {pageState === 'invalid' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Invalid Request</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage ||
                'This connection request link is invalid or has already been used.'}
            </p>
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </div>
        )}

        {/* Already Accepted State */}
        {pageState === 'already_accepted' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: FOREST_GREEN.faint }}
            >
              <Check className="h-8 w-8" style={{ color: FOREST_GREEN.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Connected</h2>
            <p className="text-muted-foreground mb-6">
              You have already accepted this connection request.
            </p>
            <Button
              onClick={() => navigate('/dashboard/connections')}
              style={{
                backgroundColor: FOREST_GREEN.DEFAULT,
                color: 'white',
              }}
            >
              View Connections
            </Button>
          </div>
        )}

        {/* Already Declined State */}
        {pageState === 'already_declined' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: MID_GRAY.lighter }}
            >
              <UserX className="h-8 w-8" style={{ color: MID_GRAY.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Already Declined</h2>
            <p className="text-muted-foreground mb-6">
              You have already declined this connection request.
            </p>
            <Button variant="outline" onClick={() => navigate('/dashboard')}>
              <Home className="h-4 w-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>
        )}

        {/* Error State */}
        {pageState === 'error' && (
          <div className="p-8 text-center">
            <div
              className="h-16 w-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ backgroundColor: ERROR.bg }}
            >
              <AlertCircle className="h-8 w-8" style={{ color: ERROR.DEFAULT }} />
            </div>
            <h2 className="text-xl font-semibold mb-2">Something Went Wrong</h2>
            <p className="text-muted-foreground mb-6">
              {errorMessage || 'An error occurred while processing your request.'}
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
              <Button onClick={() => validateToken()}>Try Again</Button>
            </div>
          </div>
        )}
      </div>

      {/* Footer text */}
      <p className="mt-8 text-sm text-muted-foreground text-center">
        If you weren't expecting this request, you can safely ignore it.
      </p>
    </div>
  );
};

export default AcceptConnectionPage;
